import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {LoaderService} from '@app/services/loader.service';
import {
  MailerServiceProvidersList,
  MailerServiceProvidersListSuccess,
  MailerServiceProvidersListError,
  MailerServiceProviderDelete,
  MailerServiceProviderDeleteSuccess,
  MailerServiceProviderDeleteError,
  MailerServiceProviderCreate,
  MailerServiceProviderCreateSuccess,
  MailerServiceProviderCreateError,
  MailerServiceProviderUpdate,
  MailerServiceProviderUpdateSuccess,
  MailerServiceProviderUpdateError,
  MailerServiceProviderSetDefault,
  MailerServiceProviderSetDefaultSuccess,
  MailerServiceProviderSetDefaultError,
} from '@app/stores/mailer-service-provider/mailer-service-provider.actions';
import {IMailerServiceProviderState} from '@app/stores/mailer-service-provider/mailer-service-provider.state';
import {MailerServiceProviderService} from '@app/services/mailer-service-provider.service';

@Injectable()
export class MailerServiceProviderEffects {
  private _actions$ = inject(Actions);
  private _store = inject<Store<IMailerServiceProviderState>>(Store);
  private providerService = inject(MailerServiceProviderService);
  private loaderService = inject(LoaderService);


  providersList$ = createEffect(() => this._actions$.pipe(
    ofType(MailerServiceProvidersList),
    switchMap((action) => {
      this.loaderService.show();
      return this.providerService.getProviders(action.params).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return MailerServiceProvidersListSuccess({paginatedMailerServiceProviders: resp.data});
          }
          this.loaderService.hide(true);
          return MailerServiceProvidersListError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(MailerServiceProvidersListError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  providerDelete$ = createEffect(() => this._actions$.pipe(
    ofType(MailerServiceProviderDelete),
    switchMap((action) => {
      this.loaderService.show();
      return this.providerService.deleteProvider(action.providerId).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return MailerServiceProviderDeleteSuccess();
          }
          this.loaderService.hide(true);
          return MailerServiceProviderDeleteError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(MailerServiceProviderDeleteError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  providerCreate$ = createEffect(() => this._actions$.pipe(
    ofType(MailerServiceProviderCreate),
    switchMap((action) => {
      this.loaderService.show();
      return this.providerService.createProvider(action.provider).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return MailerServiceProviderCreateSuccess();
          }
          this.loaderService.hide(true);
          return MailerServiceProviderCreateError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(MailerServiceProviderCreateError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  providerUpdate$ = createEffect(() => this._actions$.pipe(
    ofType(MailerServiceProviderUpdate),
    switchMap((action) => {
      this.loaderService.show();
      return this.providerService.updateProvider(action.providerId, action.provider).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return MailerServiceProviderUpdateSuccess();
          }
          this.loaderService.hide(true);
          return MailerServiceProviderUpdateError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(MailerServiceProviderUpdateError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  providerSetDefault$ = createEffect(() => this._actions$.pipe(
    ofType(MailerServiceProviderSetDefault),
    switchMap((action) => {
      this.loaderService.show();
      return this.providerService.setDefault(action.providerId).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return MailerServiceProviderSetDefaultSuccess();
          }
          this.loaderService.hide(true);
          return MailerServiceProviderSetDefaultError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(MailerServiceProviderSetDefaultError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));
}
